import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { jobsAPI } from '../services/api';
import { Job } from '../types';
import { Search, MapPin, Briefcase, Clock, DollarSign, Building, TrendingUp } from 'lucide-react';
import { FadeIn, Stagger, StaggerItem } from '../components/animations';
import { Card, CardBody, Badge } from '../components/ui';

const Jobs: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    location: '',
    employmentType: '',
    remote: false,
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchJobs = async (params?: any) => {
    setLoading(true);
    try {
      const response = await jobsAPI.getAll(params);
      setJobs(response.data.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Live search: debounce text inputs, instant on dropdown/checkbox change
  useEffect(() => {
    const params: any = {};
    if (filters.search) params.search = filters.search;
    if (filters.location) params.location = filters.location;
    if (filters.employmentType) params.employmentType = filters.employmentType;
    if (filters.remote) params.remote = 'true';
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchJobs(params), 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filters.search, filters.location, filters.employmentType, filters.remote]);

  const getTypeColor = (type: string) => {
    const colors: any = {
      'full-time': 'success',
      'part-time': 'info',
      'contract': 'warning',
      'internship': 'info',
      'freelance': 'default',
    };
    return colors[type] || 'default';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <div className="mb-10">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Find Your Dream Job</h1>
            <p className="text-lg text-gray-600">Browse thousands of verified job opportunities across Africa</p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <Card className="mb-8">
            <CardBody className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Job title or keyword"
                  />
                </div>

                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={filters.location}
                    onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="City or country"
                  />
                </div>

                <select
                  value={filters.employmentType}
                  onChange={(e) => setFilters({ ...filters, employmentType: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                >
                  <option value="">All Types</option>
                  <option value="full-time">Full Time</option>
                  <option value="part-time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="internship">Internship</option>
                  <option value="freelance">Freelance</option>
                </select>

                <button
                  onClick={() => {
                    const params: any = {};
                    if (filters.search) params.search = filters.search;
                    if (filters.location) params.location = filters.location;
                    if (filters.employmentType) params.employmentType = filters.employmentType;
                    if (filters.remote) params.remote = 'true';
                    fetchJobs(params);
                  }}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                  <Search className="w-5 h-5" />
                  Search
                </button>
              </div>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.remote}
                  onChange={(e) => setFilters({ ...filters, remote: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Remote jobs only</span>
              </label>
            </CardBody>
          </Card>
        </FadeIn>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">Loading jobs...</p>
          </div>
        ) : jobs.length === 0 ? (
          <FadeIn delay={0.2}>
            <Card className="text-center py-16">
              <CardBody>
                <Briefcase className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-gray-700 mb-2">No jobs found</h3>
                <p className="text-gray-600">Try adjusting your search filters</p>
              </CardBody>
            </Card>
          </FadeIn>
        ) : (
          <>
            <FadeIn delay={0.2}>
              <div className="mb-6 flex items-center gap-2 text-gray-600">
                <Briefcase className="w-5 h-5" />
                <span className="font-medium">{jobs.length} jobs found</span>
              </div>
            </FadeIn>

            <Stagger>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {jobs.map((job) => (
                  <StaggerItem key={job._id}>
                    <Link to={`/jobs/${job._id}`}>
                      <Card hover className="h-full group cursor-pointer">
                        <CardBody className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Briefcase className="w-6 h-6 text-blue-600" />
                            </div>
                            {job.featured && <Badge variant="warning">Featured</Badge>}
                          </div>

                          <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition line-clamp-2">
                            {job.title}
                          </h3>

                          <div className="flex items-center gap-2 mb-4">
                            <Building className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-700 font-medium">{job.business?.companyName}</span>
                          </div>

                          <div className="space-y-2 mb-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <MapPin className="w-4 h-4" />
                              <span>{job.location.city}, {job.location.country}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Clock className="w-4 h-4" />
                              <span className="capitalize">{job.employmentType}</span>
                            </div>
                            {job.salary?.min && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <DollarSign className="w-4 h-4" />
                                <span>${job.salary.min.toLocaleString()} - ${job.salary.max.toLocaleString()}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2 mb-4">
                            <Badge variant={getTypeColor(job.employmentType)}>
                              {job.employmentType}
                            </Badge>
                          </div>

                          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <span className="text-xs text-gray-500">
                              {new Date(job.createdAt).toLocaleDateString()}
                            </span>
                            <span className="text-blue-600 font-semibold text-sm group-hover:gap-2 flex items-center gap-1 transition-all">
                              View Details
                              <span className="group-hover:translate-x-1 transition-transform">→</span>
                            </span>
                          </div>
                        </CardBody>
                      </Card>
                    </Link>
                  </StaggerItem>
                ))}
              </div>
            </Stagger>
          </>
        )}
      </div>
    </div>
  );
};

export default Jobs;
